/**
 * TrackingService — Unified public API
 *
 * Fires both Mixpanel (via AnalyticsService) and GTM dataLayer (via DataLayerService)
 * in a single call. This is the primary tracking interface for all features.
 *
 * Identity is set via setUser(): Mixpanel identifies the user, and GTM receives
 * it once per document (DataLayerService.setUser) instead of on every event.
 */

import { analytics } from './analytics.service'
import { dataLayerService } from './datalayer.service'
import type {
  BeginCheckoutProps,
  FileDownloadProps,
  PurchaseProps,
  SelectContentProps,
  SelectItemProps,
  SignInProps,
  SignUpProps,
  TrackingEventMap,
  TrackingUserData,
  ViewItemListProps,
  ViewItemProps,
} from './tracking.types'

/** Cast typed props to Record for dataLayer.push() */
function toRecord(props: unknown): Record<string, unknown> {
  return props as Record<string, unknown>
}

class TrackingServiceClass {
  // useAnalytics() hands out this singleton and callers destructure its
  // methods (`const { servicePurchaseStarted } = useAnalytics()`), so every
  // method is bound here: the aliases call `this.beginCheckout` and friends.
  constructor() {
    const self = this as unknown as Record<string, unknown>
    for (const name of Object.getOwnPropertyNames(TrackingServiceClass.prototype)) {
      const member = self[name]
      if (name !== 'constructor' && typeof member === 'function') self[name] = member.bind(this)
    }
  }

  // ─── User Identity ──────────────────────────────────────────

  setUser(data: TrackingUserData): void {
    analytics.identify(data.user_id)
    analytics.setUserProperties({
      $email: data.email,
      $phone: data.phone,
      $first_name: data.first_name,
      $last_name: data.last_name,
      plan: data.plan,
      company_type: data.company_type,
      formation_state: data.formation_state,
      company_source: data.company_source,
      onboarding_status: data.onboarding_status,
    })
    dataLayerService.setUser(data)
  }

  // ─── GA4 Standard Events ───────────────────────────────────

  signIn(props: SignInProps = {}): void {
    dataLayerService.push('sign_in', toRecord(props))
  }

  signUp(props: SignUpProps): void {
    analytics.onboardingCompleted(props)
    dataLayerService.push('sign_up', toRecord(props))
  }

  beginCheckout(props: BeginCheckoutProps): void {
    analytics.checkoutStarted(props)
    dataLayerService.pushEcommerce('begin_checkout', {
      value: props.value,
      currency: props.currency,
      items: props.items || [
        {
          item_id: props.package_name || props.service_name || 'unknown',
          item_name: props.package_name || props.service_name || 'unknown',
          item_category: props.item_category,
          price: props.value,
          quantity: 1,
        },
      ],
      item_category: props.item_category,
    })
  }

  purchase(props: PurchaseProps): void {
    analytics.paymentSuccessViewed(props)
    dataLayerService.pushEcommerce('purchase', {
      transaction_id: props.transaction_id,
      // Meta deduplicates the browser and server Purchase on this id: the API's
      // server-side conversion sends `purchase_<metadata.transaction_id>` and every
      // caller passes `T_<that id>` as transaction_id (apps/api/src/analytics/server-conversions.ts).
      event_id: `purchase_${props.transaction_id.replace(/^T_/, '')}`,
      value: props.value,
      currency: props.currency,
      items: props.items || [
        {
          item_id: props.item_name || 'unknown',
          item_name: props.item_name || 'unknown',
          item_category: props.item_category,
          price: props.value,
          quantity: 1,
        },
      ],
      item_category: props.item_category,
    })
  }

  viewItem(props: ViewItemProps): void {
    analytics.serviceCardViewed(props)
    dataLayerService.push('view_item', toRecord(props))
  }

  viewItemList(props: ViewItemListProps): void {
    if (props.item_list_name === 'packages') {
      analytics.packageViewed(props)
    } else {
      analytics.servicesPageViewed(props)
    }
    dataLayerService.push('view_item_list', toRecord(props))
  }

  selectItem(props: SelectItemProps): void {
    analytics.packageSelected(props)
    dataLayerService.push('select_item', toRecord(props))
  }

  selectContent(props: SelectContentProps): void {
    if (props.content_type === 'bank_partner') {
      analytics.bankPartnerClicked(props)
    } else {
      analytics.serviceLearnMoreClicked(props)
    }
    dataLayerService.push('select_content', toRecord(props))
  }

  fileDownload(props: FileDownloadProps): void {
    analytics.mailDocumentDownloaded(props)
    dataLayerService.push('file_download', toRecord(props))
  }

  // ─── Onboarding ─────────────────────────────────────────────

  signUpStart(props: TrackingEventMap['sign_up_start']): void {
    analytics.onboardingStarted(props)
    dataLayerService.push('sign_up_start', toRecord(props))
  }

  onboardingTypeSelected(props: TrackingEventMap['onboarding_type_selected']): void {
    analytics.onboardingTypeSelected(props)
    dataLayerService.push('onboarding_type_selected', toRecord(props))
  }

  onboardingStepViewed(props: TrackingEventMap['onboarding_step_viewed']): void {
    analytics.onboardingStepViewed(props)
    dataLayerService.push('onboarding_step_viewed', toRecord(props))
  }

  onboardingStepCompleted(props: TrackingEventMap['onboarding_step_completed']): void {
    analytics.onboardingStepCompleted(props)
    dataLayerService.push('onboarding_step_completed', toRecord(props))
  }

  companyStructureSelected(props: TrackingEventMap['company_structure_selected']): void {
    analytics.companyStructureSelected(props)
    dataLayerService.push('company_structure_selected', toRecord(props))
  }

  shareholderAdded(props: TrackingEventMap['shareholder_added']): void {
    analytics.shareholderAdded(props)
    dataLayerService.push('shareholder_added', toRecord(props))
  }

  shareholderRemoved(props: TrackingEventMap['shareholder_removed']): void {
    analytics.shareholderRemoved(props)
    dataLayerService.push('shareholder_removed', toRecord(props))
  }

  documentUploaded(props: TrackingEventMap['document_uploaded']): void {
    analytics.documentUploaded(props)
    dataLayerService.push('document_uploaded', toRecord(props))
  }

  documentUploadFailed(props: TrackingEventMap['document_upload_failed']): void {
    analytics.documentUploadFailed(props)
    dataLayerService.push('document_upload_failed', toRecord(props))
  }

  einExtracted(props: TrackingEventMap['ein_extracted']): void {
    analytics.einExtracted(props)
    dataLayerService.push('ein_extracted', toRecord(props))
  }

  // ─── Dashboard ──────────────────────────────────────────────

  viewDashboard(props: TrackingEventMap['view_dashboard']): void {
    analytics.dashboardViewed(props)
    dataLayerService.push('view_dashboard', toRecord(props))
  }

  formationProgressViewed(props: TrackingEventMap['formation_progress_viewed']): void {
    analytics.formationProgressViewed(props)
    dataLayerService.push('formation_progress_viewed', toRecord(props))
  }

  quickActionClicked(props: TrackingEventMap['quick_action_clicked']): void {
    analytics.quickActionClicked(props)
    dataLayerService.push('quick_action_clicked', toRecord(props))
  }

  deadlineCardViewed(props: TrackingEventMap['deadline_card_viewed']): void {
    analytics.deadlineCardViewed(props)
    dataLayerService.push('deadline_card_viewed', toRecord(props))
  }

  // ─── Federal Tax ────────────────────────────────────────────

  federalTaxListViewed(props: TrackingEventMap['federal_tax_list_viewed']): void {
    analytics.federalTaxListViewed(props)
    dataLayerService.push('federal_tax_list_viewed', toRecord(props))
  }

  federalTaxRepresentativeNoticeViewed(props: TrackingEventMap['federal_tax_representative_notice_viewed']): void {
    analytics.federalTaxRepresentativeNoticeViewed(props)
    dataLayerService.push('federal_tax_representative_notice_viewed', toRecord(props))
  }

  federalTaxRepresentativeNoticeAccepted(props: TrackingEventMap['federal_tax_representative_notice_accepted']): void {
    analytics.federalTaxRepresentativeNoticeAccepted(props)
    dataLayerService.push('federal_tax_representative_notice_accepted', toRecord(props))
  }

  federalTaxFilingStarted(props: TrackingEventMap['federal_tax_filing_started']): void {
    analytics.federalTaxFilingStarted(props)
    dataLayerService.push('federal_tax_filing_started', toRecord(props))
  }

  federalTaxStepViewed(props: TrackingEventMap['federal_tax_step_viewed']): void {
    analytics.federalTaxStepViewed(props)
    dataLayerService.push('federal_tax_step_viewed', toRecord(props))
  }

  federalTaxStepCompleted(props: TrackingEventMap['federal_tax_step_completed']): void {
    analytics.federalTaxStepCompleted(props)
    dataLayerService.push('federal_tax_step_completed', toRecord(props))
  }

  federalTaxPartnerAdded(props: TrackingEventMap['federal_tax_partner_added']): void {
    analytics.federalTaxPartnerAdded(props)
    dataLayerService.push('federal_tax_partner_added', toRecord(props))
  }

  federalTaxFilingSubmitted(props: TrackingEventMap['federal_tax_filing_submitted']): void {
    analytics.federalTaxFilingSubmitted(props)
    dataLayerService.push('federal_tax_filing_submitted', toRecord(props))
  }

  // ─── State Tax ──────────────────────────────────────────────

  stateTaxPageViewed(props: TrackingEventMap['state_tax_page_viewed']): void {
    analytics.stateTaxPageViewed(props)
    dataLayerService.push('state_tax_page_viewed', toRecord(props))
  }

  stateTaxFilingStarted(props: TrackingEventMap['state_tax_filing_started']): void {
    analytics.stateTaxFilingStarted(props)
    dataLayerService.push('state_tax_filing_started', toRecord(props))
  }

  stateTaxSelfFiled(props: TrackingEventMap['state_tax_self_filed']): void {
    analytics.stateTaxSelfFiled(props)
    dataLayerService.push('state_tax_self_filed', toRecord(props))
  }

  stateTaxSelfFiledDeleted(props: TrackingEventMap['state_tax_self_filed_deleted']): void {
    analytics.stateTaxSelfFiledDeleted(props)
    dataLayerService.push('state_tax_self_filed_deleted', toRecord(props))
  }

  stateTaxFilingDetailViewed(props: TrackingEventMap['state_tax_filing_detail_viewed']): void {
    analytics.stateTaxFilingDetailViewed(props)
    dataLayerService.push('state_tax_filing_detail_viewed', toRecord(props))
  }

  // ─── Mailroom ───────────────────────────────────────────────

  mailroomViewed(props: TrackingEventMap['mailroom_viewed']): void {
    analytics.mailroomViewed(props)
    dataLayerService.push('mailroom_viewed', toRecord(props))
  }

  mailItemViewed(props: TrackingEventMap['mail_item_viewed']): void {
    analytics.mailItemViewed(props)
    dataLayerService.push('mail_item_viewed', toRecord(props))
  }

  mailroomAddressCopied(props: TrackingEventMap['mailroom_address_copied']): void {
    analytics.mailroomAddressCopied(props)
    dataLayerService.push('mailroom_address_copied', toRecord(props))
  }

  // ─── Company ────────────────────────────────────────────────

  companyProfileViewed(props: TrackingEventMap['company_profile_viewed']): void {
    analytics.companyProfileViewed(props)
    dataLayerService.push('company_profile_viewed', toRecord(props))
  }

  companyTabChanged(props: TrackingEventMap['company_tab_changed']): void {
    analytics.companyTabChanged(props)
    dataLayerService.push('company_tab_changed', toRecord(props))
  }

  // ─── Other ──────────────────────────────────────────────────

  bankingPageViewed(props: TrackingEventMap['banking_page_viewed']): void {
    analytics.bankingPageViewed(props)
    dataLayerService.push('banking_page_viewed', toRecord(props))
  }

  accountingPageViewed(props: TrackingEventMap['accounting_page_viewed']): void {
    analytics.accountingPageViewed(props)
    dataLayerService.push('accounting_page_viewed', toRecord(props))
  }

  connectionsPageViewed(props: TrackingEventMap['connections_page_viewed']): void {
    analytics.connectionsPageViewed(props)
    dataLayerService.push('connections_page_viewed', toRecord(props))
  }

  // ─── Bookkeeping Questions ─────────────────────────────────

  accountingQuestionsViewed(props: TrackingEventMap['accounting_questions_viewed']): void {
    analytics.accountingQuestionsViewed(props)
    dataLayerService.push('accounting_questions_viewed', toRecord(props))
  }

  accountingQuestionAnswered(props: TrackingEventMap['accounting_question_answered']): void {
    analytics.accountingQuestionAnswered(props)
    dataLayerService.push('accounting_question_answered', toRecord(props))
  }

  accountingQuestionAnswerFailed(props: TrackingEventMap['accounting_question_answer_failed']): void {
    analytics.accountingQuestionAnswerFailed(props)
    dataLayerService.push('accounting_question_answer_failed', toRecord(props))
  }

  federalTaxMembersConfirmed(props: TrackingEventMap['federal_tax_members_confirmed']): void {
    analytics.federalTaxMembersConfirmed(props)
    dataLayerService.push('federal_tax_members_confirmed', toRecord(props))
  }

  federalTaxMemberAdded(props: TrackingEventMap['federal_tax_member_added']): void {
    analytics.federalTaxMemberAdded(props)
    dataLayerService.push('federal_tax_member_added', toRecord(props))
  }

  // ─── EIN Correction (never begin_checkout/purchase: those are ad conversions) ─

  einCorrectionStarted(props: TrackingEventMap['ein_correction_started']): void {
    analytics.einCorrectionStarted(props)
    dataLayerService.push('ein_correction_started', toRecord(props))
  }

  phoneVerificationDeferred(props: TrackingEventMap['phone_verification_deferred']): void {
    analytics.phoneVerificationDeferred(props)
    dataLayerService.push('phone_verification_deferred', toRecord(props))
  }

  einCorrectionStepViewed(props: TrackingEventMap['ein_correction_step_viewed']): void {
    analytics.einCorrectionStepViewed(props)
    dataLayerService.push('ein_correction_step_viewed', toRecord(props))
  }

  einCorrectionStepCompleted(props: TrackingEventMap['ein_correction_step_completed']): void {
    analytics.einCorrectionStepCompleted(props)
    dataLayerService.push('ein_correction_step_completed', toRecord(props))
  }

  // ─── Payment ────────────────────────────────────────────────

  checkoutCompleted(props: TrackingEventMap['checkout_completed']): void {
    analytics.checkoutCompleted(props)
    dataLayerService.push('checkout_completed', toRecord(props))
  }

  paymentProcessingDelayed(props: TrackingEventMap['payment_processing_delayed']): void {
    analytics.paymentProcessingDelayed(props)
    dataLayerService.push('payment_processing_delayed', toRecord(props))
  }

  paymentProcessingFailed(props: TrackingEventMap['payment_processing_failed']): void {
    analytics.paymentProcessingFailed(props)
    dataLayerService.push('payment_processing_failed', toRecord(props))
  }

  paymentSetupRetried(props: TrackingEventMap['payment_setup_retried']): void {
    analytics.paymentSetupRetried(props)
    dataLayerService.push('payment_setup_retried', toRecord(props))
  }

  // ─── Mixpanel Utility Methods ─────────────────────────────

  registerSuperProperties(properties: Record<string, unknown>): void {
    analytics.registerSuperProperties(properties)
  }

  identify(userId: string): void {
    analytics.identify(userId)
  }

  // ─── Backward-Compatible Aliases ──────────────────────────
  // These allow existing call sites using old analytics method names
  // to work through the tracking service without code changes.

  onboardingStarted(props: TrackingEventMap['sign_up_start']): void {
    this.signUpStart(props)
  }

  onboardingCompleted(props: SignUpProps): void {
    this.signUp(props)
  }

  packageViewed(props: ViewItemListProps): void {
    this.viewItemList({ ...props, item_list_name: 'packages' })
  }

  packageSelected(props: SelectItemProps): void {
    this.selectItem(props)
  }

  checkoutStarted(props: BeginCheckoutProps): void {
    this.beginCheckout(props)
  }

  dashboardViewed(props: TrackingEventMap['view_dashboard']): void {
    this.viewDashboard(props)
  }

  servicesPageViewed(props: ViewItemListProps): void {
    this.viewItemList({ ...props, item_list_name: 'services' })
  }

  serviceCardViewed(props: ViewItemProps): void {
    this.viewItem(props)
  }

  serviceLearnMoreClicked(props: SelectContentProps): void {
    this.selectContent({ ...props, content_type: 'service_learn_more' })
  }

  servicePurchaseStarted(props: BeginCheckoutProps): void {
    this.beginCheckout({ ...props, item_category: 'service' })
  }

  mailDocumentDownloaded(props: FileDownloadProps): void {
    this.fileDownload(props)
  }

  bankPartnerClicked(props: SelectContentProps): void {
    this.selectContent({ ...props, content_type: 'bank_partner' })
  }

  paymentSuccessViewed(props: PurchaseProps): void {
    this.purchase(props)
  }

  companyDocumentViewed(props: ViewItemProps): void {
    this.viewItem({ ...props, content_type: 'document' })
  }

  companyDocumentUploaded(props: TrackingEventMap['document_uploaded']): void {
    this.documentUploaded(props)
  }

  federalTaxPurchaseStarted(props: BeginCheckoutProps): void {
    this.beginCheckout({ ...props, item_category: 'federal_tax' })
  }

  mailroomPurchaseStarted(props: BeginCheckoutProps): void {
    this.beginCheckout({ ...props, item_category: 'mailroom' })
  }
}

export const tracking = new TrackingServiceClass()
